import { ConditionalWrapper } from '~/components/ConditionalWrapper/ConditionalWrapper';
import { roundIdParamSchema, teamIdParamSchema } from '~/schemas';
import { ButtonLink } from '~/components/ButtonLink/ButtonLink';
import { useEligibleRoundResultsListQuery } from '../queries';
import { useAddRoundResultMutation } from '../mutations';
import { useRoundResultsListQuery } from '../../queries';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { Table } from '~/components/Table/Table';
import { CompetitionResult } from '~/apis/ssf';
import { useParams } from '~/hooks/useParams';
import { formatPoints } from '~/utils/points';
import { toast } from 'react-toastify';

function Row({ results }: { results: CompetitionResult }) {
  const { roundId } = useParams(roundIdParamSchema.parse);
  const { teamId } = useParams(teamIdParamSchema.parse);

  const addMutation = useAddRoundResultMutation();
  const canAddResult = !results.already_in_round;

  async function handleAdd(id: number, resultId: number) {
    await toast.promise(addMutation.mutateAsync({ id, payload: { results: [resultId] } }), {
      success: 'Resultatet har lagts till',
      pending: 'Lägger till resultat...',
      error: 'Kunde inte lägga till resultat',
    });
  }

  const { data } = useRoundResultsListQuery({ team: teamId, round: roundId });

  if (!data) {
    return false;
  }

  return (
    <Table.Row dimmed={!canAddResult}>
      <Table.Col>{results.person.license_id}</Table.Col>
      <Table.Col>{`${results.person.first_name} ${results.person.last_name}`}</Table.Col>
      <Table.Col>{formatPoints(results.points)}</Table.Col>
      <Table.Col>
        <ConditionalWrapper
          condition={!canAddResult}
          wrap={child => (
            <Tooltip title="Lyftaren har redan ett resultat tillagt för denna omgången" as="span">
              {child}
            </Tooltip>
          )}
        >
          <ButtonLink onClick={() => handleAdd(data.results[0].id, results.id)} disabled={!canAddResult}>
            Lägg till
          </ButtonLink>
        </ConditionalWrapper>
      </Table.Col>
    </Table.Row>
  );
}

export function EligibleResultsListTable() {
  const { teamId } = useParams(teamIdParamSchema.parse);
  const { roundId } = useParams(roundIdParamSchema.parse);
  const { data = [], isLoading } = useEligibleRoundResultsListQuery({ team: teamId, round: roundId });

  return (
    <Table bg="even">
      <Table.Head>
        <Table.Row>
          <Table.Col as="th">License</Table.Col>
          <Table.Col as="th">Namn</Table.Col>
          <Table.Col as="th" tooltip="Poängsystem: IPFGL">
            Poäng
          </Table.Col>
          <Table.Col as="th" />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {isLoading ? <Table.Loading cols={4} /> : data.map(results => <Row results={results} key={results.id} />)}
      </Table.Body>
    </Table>
  );
}
